import { useEffect, useState } from 'react';
import { contactSchema, type Contact } from '@forecastify/shared';
import { Button, Dialog, TextInput } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { Field } from '../card';
import { SwitchRow } from './settings-deal-dialogs';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D2 · one of a client's contacts (DM-2).           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Opened from the client dialog's *Kapcsolattartók* tab and inline from every deal contact
 * picker, so it saves on its own and hands back what the server answered — the caller only has
 * to put it in its list.
 *
 * ⚠️ **Primary is a switch, not a promise of uniqueness here.** The server demotes the client's
 * other primary in the same transaction (`contact.service.ts`), so a caller that keeps a list
 * re-reads it after a save rather than patching one row.
 */
export interface ContactDialogProps {
  readonly open: boolean;
  readonly clientId: string;
  /** `null` creates. */
  readonly contact: Contact | null;
  readonly onClose: () => void;
  /** The saved contact, as the server answered it. */
  readonly onSaved: (contact: Contact) => void;
}

/** The sentence for a refused contact write — on `code` (rule 5), never on the message. */
export async function contactRefusal(response: Response, t: Translate): Promise<string> {
  const envelope = (await response.json().catch(() => null)) as {
    error?: { code?: string; details?: { field?: string }[] };
  } | null;
  const code = envelope?.error?.code ?? 'unavailable';
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('contacts.error.notFound');
  if (code === 'permission_denied') return t('contacts.error.denied');
  if (code === 'validation_failed') {
    return envelope?.error?.details?.some((detail) => detail.field === 'email') === true
      ? t('contacts.error.email')
      : t('dealSettings.error.invalid');
  }
  return t('error.unavailable');
}

export function ContactDialog({ open, clientId, contact, onClose, onSaved }: ContactDialogProps) {
  const t = useT();
  const session = useSession();
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* A fresh form every time it opens. */
  useEffect(() => {
    if (!open) return;
    setName(contact?.name ?? '');
    setTitle(contact?.title ?? '');
    setEmail(contact?.email ?? '');
    setPhone(contact?.phone ?? '');
    setIsPrimary(contact?.isPrimary ?? false);
    setError(null);
  }, [open, contact]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('contacts.error.name'));
      return;
    }
    const mail = email.trim();
    if (mail !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/u.test(mail)) {
      setError(t('contacts.error.email'));
      return;
    }
    setBusy(true);
    const body = {
      clientId,
      name: name.trim(),
      email: mail === '' ? null : mail,
      phone: phone.trim(),
      title: title.trim(),
      isPrimary,
      ...(contact === null ? {} : { version: contact.version }),
    };
    const response = await session
      .request(contact === null ? '/contacts' : `/contacts/${contact.id}`, {
        method: contact === null ? 'POST' : 'PUT',
        body: JSON.stringify(body),
      })
      .catch(() => null);
    setBusy(false);
    if (response === null) {
      setError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      setError(await contactRefusal(response, t));
      return;
    }
    const parsed = contactSchema.safeParse(await response.json());
    if (!parsed.success) {
      setError(t('error.unavailable'));
      return;
    }
    onSaved(parsed.data);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={contact === null ? t('contacts.action.create') : t('contacts.edit')}
      description={t('contacts.hint')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('contacts.field.name')} required>
          <TextInput
            autoFocus
            value={name}
            maxLength={120}
            placeholder={t('contacts.field.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <Field label={t('contacts.field.title')}>
          <TextInput
            value={title}
            maxLength={80}
            placeholder={t('contacts.field.titlePlaceholder')}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('contacts.field.email')}>
            <TextInput
              type="email"
              value={email}
              maxLength={254}
              placeholder="nev@ceg.hu"
              onChange={(event) => {
                setEmail(event.target.value);
                setError(null);
              }}
            />
          </Field>
          <Field label={t('contacts.field.phone')}>
            <TextInput
              type="tel"
              value={phone}
              maxLength={40}
              placeholder="+36 1 234 5678"
              onChange={(event) => {
                setPhone(event.target.value);
              }}
            />
          </Field>
        </div>
        <SwitchRow
          label={t('contacts.field.primary')}
          note={t('contacts.field.primaryNote')}
          checked={isPrimary}
          onChange={setIsPrimary}
        />

        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" disabled={busy} onClick={() => void submit()}>
            {contact === null ? t('contacts.action.create') : t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
